// ─────────────────────────────────────────────────────────────
//  HCEN API Service — Correct Architecture
//
//  HCEN_Website_Gateway (fg0d020oeg) handles:
//    /hospital/* → HCEN_Website_API Lambda  (hospital dashboard)
//    /admin/*    → HCEN_Admin_API Lambda    (super admin panel)
//
//  HCEN_API (ds5hf43t45) handles:
//    → HCEN_Main_API Lambda (patient app + patient web only)
//
//  S3 bucket: hospitals.hcen.in → hosts this hospital dashboard frontend
// ─────────────────────────────────────────────────────────────

// HCEN_Website_Gateway — hospital dashboard + admin panel (BOTH via fg0d020oeg)
const API_BASE = process.env.REACT_APP_WEBSITE_API_URL
  || 'https://fg0d020oeg.execute-api.ap-southeast-2.amazonaws.com';

// HCEN_Main_API URL: https://ds5hf43t45.execute-api.ap-southeast-2.amazonaws.com/prod
// (patient app only — not used directly by this dashboard)

// Helper: get JWT token from localStorage — prefer Cognito access token, then
// legacy synthetic session token. Also try id token (carries custom attrs).
export const getToken = () =>
  localStorage.getItem('hcen_access_token')
  || localStorage.getItem('hcen_token')
  || localStorage.getItem('hcen_id_token')
  || '';

// Helper: safe fetch → returns { data, error }
const call = async (path, method = 'GET', body = null, base = API_BASE) => {
  try {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${base}${path}`, {
      method,
      headers,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const text = await res.text();
    let json;
    try { json = JSON.parse(text); } catch { json = { message: text }; }
    if (!res.ok) {
      const msg = json.message || json.error || `HTTP ${res.status}`;
      // Staff/audit 401 usually means Cognito attrs missing or expired session.
      if (res.status === 401 && path.startsWith('/hospital/staff')) {
        return { data: null, error: msg === 'Unauthorized'
          ? 'Session expired or not authorized — please log out and log in again.'
          : msg };
      }
      return { data: json, error: msg };
    }
    return { data: json, error: null };
  } catch (e) {
    return { data: null, error: e.message || 'Network error' };
  }
};

// ── AUTH ──────────────────────────────────────────────────────
// All endpoints map 1-to-1 onto the HCEN_Website_API Lambda routes.
// Phase A · May 22, 2026 — moved to AWS Cognito with lazy bcrypt fallback.
// The Lambda may return a `challenge` field (`NEW_PASSWORD_REQUIRED`
// or `NEW_PASSWORD_REQUIRED_MIGRATION`) that the React app must handle
// by routing to a "Set new password" form.
export const authAPI = {
  login: (email, password) =>
    call('/hospital/login', 'POST', { email, password }),

  // Complete the NEW_PASSWORD_REQUIRED challenge.
  respondChallenge: (email, newPassword, session) =>
    call('/hospital/auth/respond-challenge', 'POST', { email, newPassword, session }),

  // Self-service password reset (Cognito sends a 6-digit code by email).
  forgotPassword: (email) =>
    call('/hospital/auth/forgot-password', 'POST', { email }),
  confirmForgotPassword: (email, code, newPassword) =>
    call('/hospital/auth/confirm-forgot-password', 'POST', { email, code, newPassword }),

  // Authenticated change password (used in Settings).
  // Either `accessToken` (Cognito session) or `email` (bcrypt fallback) is
  // required — the Lambda picks whichever path verifies. Sending both lets
  // a single call work for both Cognito-onboarded AND legacy hospitals,
  // and additionally back-fills the Cognito user (with custom:hospitalId)
  // when only bcrypt exists.
  changePassword: (accessToken, oldPassword, newPassword, email) =>
    call('/hospital/auth/change-password', 'POST', {
      accessToken: accessToken || undefined,
      email:       email       || undefined,
      oldPassword, newPassword,
    }),

  // GET /hospital/auth/me   (Authorization: Bearer <accessToken>)
  me: () => call('/hospital/auth/me'),
};

// ── STAFF (Phase B · May 22, 2026) ────────────────────────────────────────────
// Multi-staff per hospital: one owner + N receptionists, all backed by Cognito
// users with custom:hospitalId pinning them to the same hospital. Owner-only
// endpoints are enforced server-side via the verified access token.
export const staffAPI = {
  list:    ()                   => call('/hospital/staff'),
  invite:  ({ email, name, phone }) =>
                                  call('/hospital/staff', 'POST', { email, name, phone }),
  update:  (staffId, updates)   => call(`/hospital/staff/${staffId}`, 'PATCH', updates),
  remove:  (staffId)            => call(`/hospital/staff/${staffId}`, 'DELETE'),
  resend:  (staffId)            => call(`/hospital/staff/${staffId}/resend-invite`, 'POST', {}),
  logins:  ()                   => call('/hospital/logins'),
};

// ── AUDIT LOG (Phase C · May 22, 2026) ────────────────────────────────────────
// Owner-only timeline of every meaningful action recorded by the website
// Lambda (staff invites, doctor changes, slot edits, settlement requests …).
// Backed by HCEN_HospitalAuditLog. Filter by action / since for narrower
// queries.
export const auditAPI = {
  list: ({ limit = 200, since, action } = {}) => {
    const qs = new URLSearchParams();
    if (limit)  qs.set('limit', String(limit));
    if (since)  qs.set('since', since);
    if (action) qs.set('action', action);
    return call(`/hospital/audit${qs.toString() ? `?${qs}` : ''}`);
  },
};

// ── BOOKINGS ──────────────────────────────────────────────────
export const bookingsAPI = {
  // GET /hospital/bookings?hospitalId=xxx
  getByHospital: (hospitalId) =>
    call(`/hospital/bookings?hospitalId=${hospitalId}`),

  // POST /hospital/bookings/{bookingId}/confirm
  confirm: (bookingId) =>
    call(`/hospital/bookings/${bookingId}/confirm`, 'POST'),

  // POST /hospital/bookings/{bookingId}/cancel  — passes cancelledBy so patient app shows correct label
  cancel: (bookingId, extra = {}) =>
    call(`/hospital/bookings/${bookingId}/cancel`, 'POST', {
      cancelledBy: extra.cancelledBy || 'hospital',
      cancelledByHospital: extra.cancelledByHospital || true,
      cancelledAt: new Date().toISOString(),
    }),

  // POST /hospital/bookings/{bookingId}/reschedule — 1hr min, sends WhatsApp to patient
  reschedule: (bookingId, fields = {}) =>
    call(`/hospital/bookings/${bookingId}/reschedule`, 'POST', {
      newDate:    fields.newDate    || fields.date,
      newSlot:    fields.newSlot    || fields.slot,
      reason:     fields.reason     || 'Doctor schedule change',
      doctorId:   fields.doctorId   || null,
      doctorName: fields.doctorName || null,
    }),

  // POST /hospital/bookings/send-reminders — trigger 1hr-before WhatsApp reminders
  sendReminders: (hospitalId) =>
    call('/hospital/bookings/send-reminders', 'POST', hospitalId ? { hospitalId } : {}),

  // POST /hospital/bookings/no-show — mark patient as no-show
  markNoShow: (bookingId, reason) =>
    call('/hospital/bookings/no-show', 'POST', { bookingId, reason, markedBy: 'hospital' }),
};

// ── HOSPITAL ANALYTICS ────────────────────────────────────────
export const analyticsAPI = {
  // GET /hospital/analytics?hospitalId=xxx
  getDashboard: (hospitalId) =>
    call(`/hospital/analytics?hospitalId=${hospitalId}`),
};

// ── PRESCRIPTIONS ────────────────────────────────────────────
export const prescriptionsAPI = {
  // POST /hospital/prescriptions — upload prescription
  upload: (data) =>
    call('/hospital/prescriptions', 'POST', data),
  // GET /hospital/prescriptions?bookingId=xxx
  getByBooking: (bookingId) =>
    call(`/hospital/prescriptions?bookingId=${bookingId}`),
  // GET /hospital/prescriptions/patient?patientId=xxx
  getByPatient: (patientId) =>
    call(`/hospital/prescriptions/patient?patientId=${patientId}`),
  // DELETE /hospital/prescriptions
  remove: (bookingId, prescriptionId) =>
    call('/hospital/prescriptions', 'DELETE', { bookingId, prescriptionId }),
  // POST /hospital/upload-prescription — upload a JPG/PNG/PDF, returns {url, name, type}
  // Added May 27, 2026 — replaces the old "paste a URL" UX. Reads the user's
  // selected file as base64 and ships it to the Lambda, which writes to S3
  // and returns a stable HTTPS URL the patient app/website can render.
  uploadFile: (file, { bookingId, patientId }) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve({ error: 'Failed to read file' });
    reader.onload  = async () => {
      try {
        // reader.result is a data URL: "data:<mime>;base64,<payload>"
        const dataUrl = String(reader.result || '');
        const commaIdx = dataUrl.indexOf(',');
        const fileData = commaIdx >= 0 ? dataUrl.slice(commaIdx + 1) : dataUrl;
        const r = await call('/hospital/upload-prescription', 'POST', {
          bookingId,
          patientId: patientId || '',
          fileName:  file.name || 'prescription',
          fileType:  file.type || 'application/octet-stream',
          fileData,
        });
        resolve(r);
      } catch (e) {
        resolve({ error: e.message || 'Upload failed' });
      }
    };
    reader.readAsDataURL(file);
  }),
};

// ── DOCTORS ───────────────────────────────────────────────────
export const doctorsAPI = {
  // GET /hospital/doctors?hospitalId=xxx
  getByHospital: (hospitalId) =>
    call(`/hospital/doctors?hospitalId=${hospitalId}`),

  // POST /hospital/doctors  → Add new doctor
  add: (doctorData) =>
    call('/hospital/doctors', 'POST', doctorData),

  // PATCH /hospital/doctors/{doctorId}  → Update availability or details
  update: (hospitalId, doctorId, updates) =>
    call(`/hospital/doctors/${doctorId}`, 'PATCH', updates),

  // DELETE /hospital/doctors/{doctorId}
  remove: (doctorId) =>
    call(`/hospital/doctors/${doctorId}`, 'DELETE'),

  // ── Slot management (weekly pattern) ──
  // GET /hospital/doctors/{doctorId}/slots  → returns { weeklySlots: {Mon:[], Tue:[], ...} }
  getSlots: (doctorId) =>
    call(`/hospital/doctors/${doctorId}/slots`),

  // PATCH /hospital/doctors/{doctorId}/slots  → saves entire weekly schedule
  // weeklySlots: { Mon: ['09:00','09:15',...], Tue: [], ... }
  saveSlots: (doctorId, _date, _slots, weeklySlots) =>
    call(`/hospital/doctors/${doctorId}/slots`, 'PATCH', { weeklySlots }),

  // DELETE /hospital/doctors/{doctorId}/slots?day=Mon  (or body {day})
  clearSlots: (doctorId, day) =>
    call(`/hospital/doctors/${doctorId}/slots`, 'DELETE', { day }),

  // ── Leave / Block management ──
  // GET /hospital/doctors/{doctorId}/leave?from=YYYY-MM-DD&to=YYYY-MM-DD
  getLeave: (doctorId, from, to) =>
    call(`/hospital/doctors/${doctorId}/leave?from=${from}&to=${to}`),
  // POST /hospital/doctors/{doctorId}/leave  { dates, type, reason }
  addLeave: (doctorId, dates, type = 'leave', reason = '') =>
    call(`/hospital/doctors/${doctorId}/leave`, 'POST', { dates, type, reason }),
  // DELETE /hospital/doctors/{doctorId}/leave  { dates }
  removeLeave: (doctorId, dates) =>
    call(`/hospital/doctors/${doctorId}/leave`, 'DELETE', { dates }),
};

// ── REVENUE ───────────────────────────────────────────────────
export const revenueAPI = {
  // GET /hospital/revenue?hospitalId=xxx
  get: (hospitalId) =>
    call(`/hospital/revenue?hospitalId=${hospitalId}`),
};

// ── BEDS ──────────────────────────────────────────────────────
export const bedsAPI = {
  // PUT /hospital/beds  → { hospitalId, totalBeds, occupiedBeds, icuTotal, icuOccupied, nicuTotal, nicuOccupied }
  update: (data) =>
    call('/hospital/beds', 'PATCH', data),
};

// ── EMERGENCY ALERTS ──────────────────────────────────────────
export const emergencyAPI = {
  // GET /hospital/emergencies?hospitalId=xxx
  getByHospital: (hospitalId) =>
    call(`/hospital/emergencies?hospitalId=${hospitalId}`),

  // POST /hospital/emergencies/{id}/acknowledge
  acknowledge: (alertId, { hospitalId, hospitalName, hospitalPhone } = {}) =>
    call(`/hospital/emergencies/${alertId}/acknowledge`, 'POST', {
      hospitalId, hospitalName, hospitalPhone,
    }),

  // POST /hospital/emergencies/{id}/handle
  handle: (alertId) =>
    call(`/hospital/emergencies/${alertId}/handle`, 'POST'),

  // PATCH /hospital/emergencies/{id}/prepare — record prep checklist
  prepare: (alertId, data) =>
    call(`/hospital/emergencies/${alertId}/prepare`, 'PATCH', data),

  // POST /hospital/emergencies/{id}/decline — broadcast SOS: hospital opts out
  decline: (alertId, data) =>
    call(`/hospital/emergencies/${alertId}/decline`, 'POST', data),

  // POST /hospital/emergencies/{id}/cancel — direct alert: hospital declines
  cancel: (alertId, data) =>
    call(`/hospital/emergencies/${alertId}/cancel`, 'POST', data),
};

// ── REVIEWS ───────────────────────────────────────────────────
export const reviewsAPI = {
  getSummary: (hospitalId) =>
    call(`/hospital/reviews/summary?hospitalId=${encodeURIComponent(hospitalId)}`),
  list: (hospitalId, { star, limit = 100 } = {}) => {
    const qs = new URLSearchParams({ hospitalId, limit: String(limit) });
    if (star) qs.set('star', String(star));
    return call(`/hospital/reviews?${qs}`);
  },
};

// ── POSTS / PROFILE ───────────────────────────────────────────
export const postsAPI = {
  getProfile: (hospitalId) =>
    call(`/hospital/profile?hospitalId=${encodeURIComponent(hospitalId)}`),
  save: (hospitalId, postImages) =>
    call('/hospital/posts', 'PATCH', { hospitalId, postImages }),
};

// ── HEALTH OFFERS (hospital submits, admin approves) ──────────
export const offersAPI = {
  list: (hospitalId) =>
    call(`/hospital/offers?hospitalId=${encodeURIComponent(hospitalId)}`),
  create: (data) =>
    call('/hospital/offers', 'POST', data),
  update: (offerId, data) =>
    call(`/hospital/offers/${offerId}`, 'PATCH', data),
  remove: (offerId, hospitalId) =>
    call(`/hospital/offers/${offerId}?hospitalId=${encodeURIComponent(hospitalId)}`, 'DELETE'),
  uploadImage: (data) =>
    call('/hospital/upload-image', 'POST', data),
};

// ── ADMIN HEALTH OFFERS ───────────────────────────────────────
export const adminOffersAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== 'All States')
    ).toString();
    return adminCall(`/admin/offers${qs ? `?${qs}` : ''}`);
  },
  create: (data) => adminCall('/admin/offers', 'POST', data),
  update: (offerId, data) => adminCall(`/admin/offers/${offerId}`, 'PATCH', data),
  remove: (offerId) => adminCall(`/admin/offers/${offerId}`, 'DELETE'),
};

// ── ADMIN SUPPORT TICKETS & REFERRALS (same gateway — no cross-origin) ──
export const adminTicketsAPI = {
  list: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== 'all')
    ).toString();
    return adminCall(`/admin/tickets${qs ? `?${qs}` : ''}`);
  },
  get: (ticketId) => adminCall(`/admin/tickets/${encodeURIComponent(ticketId)}`),
  update: (ticketId, data) =>
    adminCall(`/admin/tickets/${encodeURIComponent(ticketId)}/update`, 'POST', data),
  addMessage: (ticketId, data) =>
    adminCall(`/admin/tickets/${encodeURIComponent(ticketId)}/messages`, 'POST', data),
  addNote: (ticketId, data) =>
    adminCall(`/admin/tickets/${encodeURIComponent(ticketId)}/notes`, 'POST', data),
};

export const adminReferralsAPI = {
  list: () => adminCall('/admin/referrals'),
};

// ── HOSPITAL SETTINGS ─────────────────────────────────────────
export const settingsAPI = {
  // GET /hospital/settings?hospitalId=xxx
  get: (hospitalId) =>
    call(`/hospital/settings?hospitalId=${hospitalId}`),

  // PUT /hospital/settings → { hospitalId, departments, facilities, emergencyPhone, mainPhone, ... }
  update: (data) =>
    call('/hospital/settings', 'PATCH', data),
};



const getAdminToken = () => localStorage.getItem('hcen_admin_token') || '';
const getAdminRefreshToken = () => localStorage.getItem('hcen_admin_refresh_token') || '';

const _clearAdminSession = () => {
  localStorage.removeItem('hcen_admin_token');
  localStorage.removeItem('hcen_admin_refresh_token');
  localStorage.removeItem('hcen_admin_email');
  localStorage.removeItem('hcen_admin');
  window.dispatchEvent(new CustomEvent('hcen-admin-session-expired'));
};

// One refresh in-flight so parallel 401s (e.g. tab open firing many APIs) share a result.
let _adminRefreshPromise = null;

const _tryRefreshAdminSession = async () => {
  const refreshToken = getAdminRefreshToken();
  if (!refreshToken) return false;
  if (_adminRefreshPromise) return _adminRefreshPromise;

  _adminRefreshPromise = (async () => {
    try {
      let email = localStorage.getItem('hcen_admin_email') || '';
      if (!email) {
        try {
          email = JSON.parse(localStorage.getItem('hcen_admin') || '{}').email || '';
        } catch { /* ignore */ }
      }
      email = String(email || '').toLowerCase().trim();
      const res = await fetch(`${API_BASE}/admin/login/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken, email }),
      });
      const text = await res.text();
      let json;
      try { json = JSON.parse(text); } catch { json = {}; }
      if (!res.ok || !json?.token) return false;
      localStorage.setItem('hcen_admin_token', json.token);
      // Cognito usually keeps the same refresh token; only overwrite if a new one is returned.
      if (json.refreshToken) {
        localStorage.setItem('hcen_admin_refresh_token', json.refreshToken);
      }
      if (json.admin) {
        localStorage.setItem('hcen_admin', JSON.stringify(json.admin));
        if (json.admin.email) {
          localStorage.setItem('hcen_admin_email', String(json.admin.email).toLowerCase().trim());
        }
      }
      return true;
    } catch {
      return false;
    } finally {
      _adminRefreshPromise = null;
    }
  })();

  return _adminRefreshPromise;
};

const adminCall = async (path, method = 'GET', body = null, base = API_BASE, _retried = false) => {
  try {
    const token = getAdminToken();
    const headers = {
      'Content-Type':  'application/json',
      'X-Admin-Token': token,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const res = await fetch(`${base}${path}`, {
      method,
      headers,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const text = await res.text();
    let json;
    try { json = JSON.parse(text); } catch { json = { message: text }; }

    // Cognito AccessTokens expire (~1h). Try refresh ONCE before kicking to login —
    // this was the page-click → login glitch (Jul 2026).
    if (res.status === 401 && !String(path).includes('/admin/login')) {
      if (!_retried && getAdminRefreshToken()) {
        const ok = await _tryRefreshAdminSession();
        if (ok) return adminCall(path, method, body, base, true);
      }
      // Break-glass HMAC (no refresh) or refresh failed → must re-login.
      _clearAdminSession();
      return { data: null, error: 'Session expired — please log out and sign in again.' };
    }
    if (!res.ok) return { data: null, error: json.error || json.message || `HTTP ${res.status}` };
    return { data: json, error: null };
  } catch (e) {
    return { data: null, error: e.message || 'Network error' };
  }
};

export const adminAPI = {
  // POST /admin/login — No token needed, just credentials
  login: (email, password) =>
    call('/admin/login', 'POST', { email, password }),

  // GET /admin/hospitals → list all hospitals
  listHospitals: () =>
    adminCall('/admin/hospitals'),

  // POST /admin/hospitals → create hospital (Cognito + DynamoDB)
  createHospital: (data) =>
    adminCall('/admin/hospitals', 'POST', data),

  // PATCH /admin/hospitals/{id} → update status/details
  updateHospital: (hospitalId, updates) =>
    adminCall(`/admin/hospitals/${hospitalId}`, 'PATCH', updates),

  // Cross-account link with Networks HMS (IP) — separate AWS accounts, HMAC pairing codes
  generateIpLink: (hospitalId) =>
    adminCall(`/admin/hospitals/${hospitalId}/ip-link/generate`, 'POST', {}),
  acceptIpLink: (hospitalId, body) =>
    adminCall(`/admin/hospitals/${hospitalId}/ip-link/accept`, 'POST', body || {}),
  unlinkIp: (hospitalId) =>
    adminCall(`/admin/hospitals/${hospitalId}/ip-link/unlink`, 'POST', {}),

  // POST /admin/hospitals/{id}/verify-bank → mark bank account verified (BOOL in DynamoDB)
  verifyBank: (hospitalId) =>
    adminCall(`/admin/hospitals/${hospitalId}/verify-bank`, 'POST', {}),

  // DELETE /admin/hospitals/{id}
  deleteHospital: (hospitalId) =>
    adminCall(`/admin/hospitals/${hospitalId}`, 'DELETE'),

  // POST /admin/hospitals/{id}/reinvite-owner → resends Cognito welcome email
  reinviteOwner: (hospitalId) =>
    adminCall(`/admin/hospitals/${hospitalId}/reinvite-owner`, 'POST'),

  // POST /admin/hospitals/{id}/reset-password
  resetPassword: (hospitalId) =>
    adminCall(`/admin/hospitals/${hospitalId}/reset-password`, 'POST'),

  // GET /admin/analytics → platform-wide stats
  getAnalytics: () =>
    adminCall('/admin/analytics'),

  // ── BOOKINGS ──────────────────────────────────────────────────
  // GET /admin/bookings → all bookings
  getBookings: () =>
    adminCall('/admin/bookings'),

  // PATCH /admin/bookings/{id}
  updateBooking: (bookingId, updates) =>
    adminCall(`/admin/bookings/${bookingId}`, 'PATCH', updates),

  // DELETE /admin/bookings/{id}
  deleteBooking: (bookingId) =>
    adminCall(`/admin/bookings/${bookingId}`, 'DELETE'),

  // ── PATIENTS ──────────────────────────────────────────────────
  // GET /admin/patients → unique patients from bookings
  getPatients: () =>
    adminCall('/admin/patients'),

  // ── DOCTORS ───────────────────────────────────────────────────
  // GET /admin/doctors → all doctors
  getDoctors: () =>
    adminCall('/admin/doctors'),

  // PATCH /admin/doctors/{id}
  updateDoctor: (doctorId, updates) =>
    adminCall(`/admin/doctors/${doctorId}`, 'PATCH', updates),

  // DELETE /admin/doctors/{id}
  deleteDoctor: (doctorId) =>
    adminCall(`/admin/doctors/${doctorId}`, 'DELETE'),

  // ── EMERGENCIES ───────────────────────────────────────────────
  // GET /admin/emergencies → all emergency alerts
  getEmergencies: () =>
    adminCall('/admin/emergencies'),

  // PATCH /admin/emergencies/{id}
  updateEmergency: (alertId, status) =>
    adminCall(`/admin/emergencies/${alertId}`, 'PATCH', { status }),

  // ── HOSPITAL DETAIL ───────────────────────────────────────────
  // GET /admin/hospitals/{id}/details → full hospital data
  getHospitalDetails: (hospitalId) =>
    adminCall(`/admin/hospitals/${hospitalId}/details`),

  // ── SETTLEMENT ────────────────────────────────────────────────
  // POST /admin/hospitals/{id}/settlement → record payment.
  // `txnRef` (optional) lets the admin tag the row with the bank UTR so it
  // appears in the unified settlement ledger and on the hospital portal.
  recordSettlement: (hospitalId, amount, note, txnRef) =>
    adminCall(`/admin/hospitals/${hospitalId}/settlement`, 'POST', { amount, note, txnRef }),

  // POST /admin/settlements/auto-run        → trigger weekly Friday batch now
  // POST /admin/settlements/auto-run?dryRun=1 → preview without writes
  runAutoSettlement: ({ dryRun = false } = {}) =>
    adminCall(`/admin/settlements/auto-run${dryRun ? '?dryRun=1' : ''}`, 'POST', {}),

  // ── DOCTOR MANAGEMENT FROM ADMIN ──────────────────────────────
  // POST /admin/hospitals/{id}/doctors → create doctor for hospital
  createDoctor: (hospitalId, doctorData) =>
    adminCall(`/admin/hospitals/${hospitalId}/doctors`, 'POST', doctorData),

  // ── FACILITIES & EQUIPMENT ──────────────────────────────────
  // PATCH /admin/hospitals/{id} with array fields (equipment, facilities, departments)
  updateFacilities: (hospitalId, data) =>
    adminCall(`/admin/hospitals/${hospitalId}`, 'PATCH', data),

  // PATCH /admin/hospitals/{id} with bed field updates
  updateBeds: (hospitalId, data) =>
    adminCall(`/admin/hospitals/${hospitalId}`, 'PATCH', data),

  // ── INTELLIGENCE / ANALYTICS ──────────────────────────────────
  // GET /admin/patient-timeline?phone=... → full user lifecycle
  getPatientTimeline: (phone, name) => {
    const qs = phone ? `phone=${encodeURIComponent(phone)}` : `name=${encodeURIComponent(name)}`;
    return adminCall(`/admin/patient-timeline?${qs}`);
  },

  // GET /admin/activity-feed?limit=60 → platform-wide live event feed + charts
  getActivityFeed: (limit = 60) =>
    adminCall(`/admin/activity-feed?limit=${limit}`),

  // GET /admin/hospitals/:id/activity → hospital-specific timeline & 30-day chart
  getHospitalActivity: (hospitalId) =>
    adminCall(`/admin/hospitals/${hospitalId}/activity`),

  // ── TRANSACTIONS & REFUNDS ────────────────────────────────────
  // GET /admin/transactions?type=&status=&from=&to=&hospitalId=&q=&limit=
  listTransactions: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== '')
    ).toString();
    return adminCall(`/admin/transactions${qs ? `?${qs}` : ''}`);
  },
  getTransaction: (txnId) =>
    adminCall(`/admin/transactions/${txnId}`),
  initiateRefund: (txnId) =>
    adminCall(`/admin/transactions/${txnId}/initiate`, 'POST'),
  issueRefundFromPayment: (txnId, { bookingId, reason, amount, adminEmail, force } = {}) =>
    adminCall(`/admin/transactions/${txnId}/issue-refund`, 'POST', {
      bookingId, reason, amount, adminEmail, force,
    }),
  markRefundCredited: (txnId, { adminEmail, note } = {}) =>
    adminCall(`/admin/transactions/${txnId}/mark-credited`, 'POST', { adminEmail, note }),
  syncRazorpay: (txnId) =>
    adminCall(`/admin/transactions/${txnId}/sync-razorpay`, 'POST', {}),
  addTransactionNote: (txnId, note, adminEmail) =>
    adminCall(`/admin/transactions/${txnId}/note`, 'POST', { note, adminEmail }),
  createManualRefund: ({ bookingId, reason, amount, adminEmail, force }) =>
    adminCall('/admin/transactions/refund', 'POST', {
      bookingId, reason, amount, adminEmail, force,
    }),
  getRefundMode: () =>
    adminCall('/admin/refund-mode'),
  setRefundMode: (mode) =>
    adminCall('/admin/refund-mode', 'POST', { mode }),

  // POST /admin/transactions/backfill — one-shot recovery: synthesises
  // payment+refund rows for every historical booking that pre-dates the
  // Transactions table. Idempotent; safe to call repeatedly.
  backfillTransactions: () =>
    adminCall('/admin/transactions/backfill', 'POST'),

  // ── PLATFORM SETTINGS (commission, payout rules) ──────────────
  getPlatformSettings: () =>
    adminCall('/admin/platform-settings'),
  updatePlatformSettings: (settings) =>
    adminCall('/admin/platform-settings', 'POST', settings),
  getHospitalCommissions: () =>
    adminCall('/admin/platform-settings/hospital-commissions'),
  // Update per-hospital commission (uses existing PATCH /admin/hospitals/:id)
  updateHospitalCommission: (hospitalId, commData) =>
    adminCall(`/admin/hospitals/${hospitalId}`, 'PATCH', commData),

  // ── CAMPAIGNS (Push Notifications) ──────────────────────────
  sendCampaign: (data) =>
    adminCall('/admin/campaigns/send', 'POST', data),
  getCampaignHistory: () =>
    adminCall('/admin/campaigns/history'),

  // ── REPORTS (Email Digest) ───────────────────────────────────
  sendDailyReport: () =>
    adminCall('/admin/reports/send-daily', 'POST'),
  sendWeeklyReport: () =>
    adminCall('/admin/reports/send-weekly', 'POST'),

  // ── PROMO CODES ──────────────────────────────────────────────
  getPromoCodes: () =>
    adminCall('/admin/promo-codes'),
  createPromoCode: (data) =>
    adminCall('/admin/promo-codes', 'POST', data),
  updatePromoCode: (code, data) =>
    adminCall(`/admin/promo-codes/${encodeURIComponent(code)}`, 'PATCH', data),
  deletePromoCode: (code) =>
    adminCall(`/admin/promo-codes/${encodeURIComponent(code)}`, 'DELETE'),

  // ── AUDIT LOG ─────────────────────────────────────────────────
  getAuditLog: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== '')
    ).toString();
    return adminCall(`/admin/audit-log${qs ? `?${qs}` : ''}`);
  },

  // ── TEAM LOGINS (owner-only) ──────────────────────────────────
  getAdminUsers: () =>
    adminCall('/admin/admin-users'),
  createAdminUser: (data) =>
    adminCall('/admin/admin-users', 'POST', data),
  updateAdminUser: (email, data) =>
    adminCall(`/admin/admin-users/${encodeURIComponent(email)}`, 'PATCH', data),
  deleteAdminUser: (email) =>
    adminCall(`/admin/admin-users/${encodeURIComponent(email)}`, 'DELETE'),

  // ── MFA self-service (any signed-in Cognito admin) ────────────
  mfaSetup: () =>
    adminCall('/admin/mfa/setup', 'POST'),
  mfaVerify: (code) =>
    adminCall('/admin/mfa/verify', 'POST', { code }),
  mfaDisable: () =>
    adminCall('/admin/mfa/disable', 'POST'),
};

// ── HOSPITAL BANKING API (hospital self-submits bank details) ───
// Hospital calls this from their Settings page using their JWT
export const bankAPI = {
  // GET /hospital/banking → get own bank details (hospitalId optional — JWT fallback on Lambda)
  getDetails: (hospitalId) =>
    call(`/hospital/banking${hospitalId ? `?hospitalId=${encodeURIComponent(hospitalId)}` : ''}`),

  // POST /hospital/banking → submit/update bank details
  // PRODUCTION FIX (May 17, 2026): switched from PATCH to POST because the
  // API Gateway resource for /hospital/banking only had POST method
  // registered, which made PATCH calls 404 from the Settlements tab.
  // The Lambda now accepts either method.
  submitDetails: (bankData) =>
    call('/hospital/banking', 'POST', bankData),
};

// ── SETTLEMENT REQUEST API (hospital raises, admin manages) ─────
// Settlement routes live on HCEN_Website_Gateway (fg0d020oeg)
//   /hospital/settlement-requests → HCEN_Website_API Lambda
//   /admin/settlement-requests    → HCEN_Admin_API Lambda
export const settlementAPI = {
  // Hospital: POST /hospital/settlement-requests → raise new request
  raise: (data) =>
    call('/hospital/settlement-requests', 'POST', data),

  // Hospital: GET /hospital/settlement-requests?hospitalId= → own history
  getMyRequests: (hospitalId) =>
    call(`/hospital/settlement-requests?hospitalId=${hospitalId}`),

  // Hospital: GET /hospital/settlement-statement?hospitalId= → CSV + summary for download
  getStatement: (hospitalId) =>
    call(`/hospital/settlement-statement?hospitalId=${hospitalId}`),
};

// Admin settlement management — uses adminCall (X-Admin-Token) via API_BASE (fg0d020oeg)
export const adminSettlementAPI = {
  // GET /admin/settlement-requests → all requests
  getAll: () =>
    adminCall('/admin/settlement-requests'),

  // PATCH /admin/settlement-requests/{id} → disburse / reject / processing
  update: (requestId, updates) =>
    adminCall(`/admin/settlement-requests/${requestId}`, 'PATCH', updates),
};

// ── HCEN_Patients (canonical patient identity, May 2026 permanent fix) ───
export const patientsAPI = {
  // GET /hospital/patients/{phone} → fetch canonical patient profile
  get: (phone) =>
    call(`/hospital/patients/${encodeURIComponent(phone)}`),

  // PATCH /hospital/patients/{phone} → audited correction
  // Body: { patientName?, patientAge?, patientGender?, patientAddress?, updatedBy? }
  update: (phone, patch) =>
    call(`/hospital/patients/${encodeURIComponent(phone)}`, 'PATCH', patch),
};

// ── HOSPITAL APPLICATIONS (Public registration) ─────────────────
let _hospitalAppSession = { token: null, email: null };

const APP_ID_KEY = 'hcenAppId';
const APP_SESSION_KEY = 'hcenAppSession';
const APP_EMAIL_KEY = 'hcenAppEmail';

/** Clear application session (tab + legacy localStorage). Call on fresh apply / after submit. */
export function clearHospitalAppSession() {
  _hospitalAppSession = { token: null, email: null };
  try {
    sessionStorage.removeItem(APP_ID_KEY);
    sessionStorage.removeItem(APP_SESSION_KEY);
    sessionStorage.removeItem(APP_EMAIL_KEY);
    localStorage.removeItem(APP_ID_KEY);
    localStorage.removeItem(APP_SESSION_KEY);
    localStorage.removeItem(APP_EMAIL_KEY);
  } catch (_) {}
}

export function getStoredApplicationId() {
  try {
    return sessionStorage.getItem(APP_ID_KEY);
  } catch (_) {
    return null;
  }
}

export function setHospitalAppSession(token, email, applicationId = null) {
  _hospitalAppSession = { token: token || null, email: email || null };
  try {
    if (token) sessionStorage.setItem(APP_SESSION_KEY, token);
    else sessionStorage.removeItem(APP_SESSION_KEY);
    if (email) sessionStorage.setItem(APP_EMAIL_KEY, email);
    else sessionStorage.removeItem(APP_EMAIL_KEY);
    if (applicationId) sessionStorage.setItem(APP_ID_KEY, applicationId);
  } catch (_) {}
}

export function loadHospitalAppSession() {
  try {
    const token = sessionStorage.getItem(APP_SESSION_KEY);
    const email = sessionStorage.getItem(APP_EMAIL_KEY);
    if (token || email) _hospitalAppSession = { token, email };
  } catch (_) {}
  return _hospitalAppSession;
}

const _appAuth = (body = {}) => ({
  ...body,
  sessionToken: _hospitalAppSession.token || undefined,
  email:        _hospitalAppSession.email || body.email || undefined,
});

export const hospitalAPI = {
  // POST /hospital/verify-email — Send OTP verification email
  verifyEmail: (email, name) =>
    call('/hospital/verify-email', 'POST', { email, name }),
  
  // POST /hospital/verify-otp — Verify OTP and create application
  verifyOTP: (email, otp, name, hospitalName) =>
    call('/hospital/verify-otp', 'POST', { email, otp, name, hospitalName }),

  requestResumeOtp: (applicationId) =>
    call('/hospital/resume/request-otp', 'POST', { applicationId: String(applicationId || '').trim().toUpperCase() }),

  verifyResumeOtp: (applicationId, otp) =>
    call('/hospital/resume/verify-otp', 'POST', {
      applicationId: String(applicationId || '').trim().toUpperCase(),
      otp,
    }),

  submitContactInquiry: (payload) =>
    call('/hospital/contact-inquiry', 'POST', payload),
  
  // GET /hospital/application/{id} — Get application for resuming
  getApplication: (applicationId) => {
    const qs = new URLSearchParams();
    if (_hospitalAppSession.token) qs.set('sessionToken', _hospitalAppSession.token);
    if (_hospitalAppSession.email) qs.set('email', _hospitalAppSession.email);
    const q = qs.toString();
    return call(`/hospital/application/${applicationId}${q ? `?${q}` : ''}`);
  },
  
  // POST /hospital/save-draft — Save partial application
  saveDraft: (applicationId, currentStep, formData, isFinal = false) =>
    call('/hospital/save-draft', 'POST', _appAuth({
      applicationId,
      currentStep,
      formData,
      isFinal,
      email: formData?.email,
    })),
  
  // POST /hospital/apply — Submit final application with documents
  submitApplication: (formData) =>
    call('/hospital/apply', 'POST', _appAuth(formData)),
  
  // ── KYC verification (Cashfree) ──────────────────────────────────────
  // Each call hits Cashfree synchronously via the Website Lambda. If
  // applicationId is supplied, the result is persisted onto
  // HospitalApplications.kyc.{gst|pan|bank} so admin sees the same status.
  // Response shape: { verified: bool, status, details, error?, ... }
  verifyGST: (gstNumber, applicationId = null) =>
    call('/hospital/verify-gst', 'POST', { gstNumber, applicationId }),

  verifyPAN: (panNumber, name = null, applicationId = null) =>
    call('/hospital/verify-pan', 'POST', { panNumber, name, applicationId }),

  verifyBank: (accountNumber, ifsc, name = null, applicationId = null) =>
    call('/hospital/verify-bank', 'POST', { accountNumber, ifsc, name, applicationId }),

  verifyCIN: (cinNumber, applicationId = null) =>
    call('/hospital/verify-cin', 'POST', { cinNumber, applicationId }),

  kycHealth: () => call('/hospital/kyc-health'),

  getAgreementPreview: (applicationId) => {
    const qs = new URLSearchParams();
    if (applicationId) qs.set('applicationId', applicationId);
    if (_hospitalAppSession.token) qs.set('sessionToken', _hospitalAppSession.token);
    if (_hospitalAppSession.email) qs.set('email', _hospitalAppSession.email);
    return call(`/hospital/agreement-preview?${qs.toString()}`);
  },

  getAgreementPdf: (applicationId) => {
    const qs = new URLSearchParams();
    if (applicationId) qs.set('applicationId', applicationId);
    if (_hospitalAppSession.token) qs.set('sessionToken', _hospitalAppSession.token);
    if (_hospitalAppSession.email) qs.set('email', _hospitalAppSession.email);
    return call(`/hospital/agreement-pdf?${qs.toString()}`);
  },

  kycInitiate: (aadhaarNumber, applicationId = null) =>
    call('/hospital/kyc/initiate', 'POST', _appAuth({ aadhaarNumber, applicationId })),

  kycVerify: (sessionId, otp, aadhaarNumber, applicationId = null) =>
    call('/hospital/kyc/verify', 'POST', _appAuth({ sessionId, otp, aadhaarNumber, applicationId })),

  // Legacy aliases (same routes on Lambda)
  okycInitiate: (aadhaarNumber, applicationId = null) =>
    call('/hospital/kyc/initiate', 'POST', _appAuth({ aadhaarNumber, applicationId })),

  okycVerify: (sessionId, otp, aadhaarNumber, applicationId = null) =>
    call('/hospital/kyc/verify', 'POST', _appAuth({ sessionId, otp, aadhaarNumber, applicationId })),

  digilockerInitiate: (applicationId, mobileNumber, redirectUrl = null) =>
    call('/hospital/digilocker/initiate', 'POST', _appAuth({ applicationId, mobileNumber, redirectUrl })),

  digilockerFinish: (applicationId, code, state, codeVerifier, recordId = null) =>
    call('/hospital/digilocker/finish', 'POST', _appAuth({
      applicationId, code, state, codeVerifier, recordId,
    })),

  esignInitiate: (applicationId, returnUrl = null, signerMobile = null) =>
    call('/hospital/esign/initiate', 'POST', _appAuth({ applicationId, returnUrl, signerMobile })),

  esignComplete: (applicationId, refs = {}) =>
    call('/hospital/esign/complete', 'POST', _appAuth({
      applicationId,
      invitationLink: refs.invitationLink || null,
      transactionId: refs.transactionId || null,
      docketId: refs.docketId || null,
      documentId: refs.documentId || null,
    })),

  // Helper: Upload document to S3 (returns URL)
  uploadDocument: async (applicationId, docKey, file) => {
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(file);
      });
      
      const r = await call('/hospital/upload-document', 'POST', _appAuth({
        applicationId,
        documentKey: docKey,
        fileName: file.name,
        fileType: file.type,
        fileData: base64,
      }));
      
      return r.data?.url || null;
    } catch (e) {
      console.error('Upload error:', e);
      return null;
    }
  },
};

// Admin: Manage hospital applications
export const adminApplicationsAPI = {
  // GET /admin/applications → all pending applications
  getAll: () => adminCall('/admin/applications'),
  
  // GET /admin/applications/{id} → single application details
  getById: (id) => adminCall(`/admin/applications/${id}`),
  
  // PATCH /admin/applications/{id} → approve/reject with notes
  update: (id, updates) => adminCall(`/admin/applications/${id}`, 'PATCH', updates),
};

export const adminContactInquiriesAPI = {
  list: () => adminCall('/admin/contact-inquiries'),
  update: (inquiryId, updates) =>
    adminCall(`/admin/contact-inquiries/${encodeURIComponent(inquiryId)}`, 'PATCH', updates),
};


